import { user } from "@/repositories/user";
import { StateCreator } from "zustand";
import { User as AuthUser } from "firebase/auth";
import { AuthCustomClaims, AppInfo, Role, Shop, User } from "@/types/v2";
import { ListenersSlice } from "@/stores/listeners.slice";
import { analyticsRepository } from "@/repositories/analytics";
import { config } from "@/config";
import { appInfoRepository } from "@/repositories/appInfoRepository";

export interface UserSlice {
  user: User | undefined;
  subscribeUser: (authUser: AuthUser) => Promise<void>;
  allowedShops: Shop[];
  resetUser: () => void;
  appInfo: AppInfo | undefined;
  subscribeAppInfo: () => void;
}

const initialSlice: UserSlice = {
  user: undefined,
  allowedShops: [],
  subscribeUser: async () => {},
  resetUser: () => {},
  appInfo: undefined,
  subscribeAppInfo: () => {},
};

export const createUserSlice: StateCreator<
  UserSlice & ListenersSlice,
  [],
  [],
  UserSlice
> = (set, get) => ({
  ...initialSlice,
  resetUser: () => {
    get().unsubscribeUserListener();
    set({ user: undefined, allowedShops: [] });
  },

  subscribeUser: async (authUser: AuthUser) => {
    // Check if user in DB, if not, create
    const dbUser = await user.getById(authUser.uid);
    const tokenResult = await authUser.getIdTokenResult();
    const claims = tokenResult.claims as Partial<AuthCustomClaims> | undefined;
    const role = (
      claims?.superAdmin
        ? "superAdmin"
        : claims?.role
          ? (claims.role ?? "user")
          : "user"
    ) as Role;

    if (!dbUser) {
      await user.create({
        id: authUser.uid,
        uid: authUser.uid,
        displayName: authUser.displayName ?? "",
        email: authUser.email ?? "",
        emailVerified: authUser.emailVerified,
        creationTime: authUser.metadata.creationTime,
        lastLoginTime: new Date(),
        role: role ?? "user",
      });
    } else {
      await user.update({
        id: authUser.uid,
        uid: authUser.uid,
        lastLoginTime: new Date(),
        role: role ?? "user",
      });
    }

    analyticsRepository.setAnalyticsUserId(authUser.uid);
    analyticsRepository.setAnalyticsUserProperty("email", authUser.email ?? "");
    analyticsRepository.setAnalyticsUserProperty(
      "email_verified",
      authUser.emailVerified ? "true" : "false"
    );

    analyticsRepository.setAnalyticsUserProperty("role", role ?? "user");
    analyticsRepository.setAnalyticsUserProperty("app_version", config.version);

    const subscription = user.subscribe(authUser.uid, async (doc) => {
      if (!doc) {
        set({ user: undefined, allowedShops: [] });
        return;
      }
      const allowedShops = await user.getLinkedShops(doc.id, role);

      analyticsRepository.setAnalyticsUserProperty("role", doc.role);
      analyticsRepository.setAnalyticsUserProperty(
        "first_name",
        doc.firstName ?? ""
      );
      analyticsRepository.setAnalyticsUserProperty(
        "last_name",
        doc.lastName ?? ""
      );

      set({
        user: doc,
        allowedShops: [
          ...allowedShops.sort((a, b) => a.name.localeCompare(b.name)),
        ],
      });
    });
    get().setUserListener(subscription);
    return;
  },
  subscribeAppInfo: () => {
    const unsubscribe = appInfoRepository.subscribe((appInfo) => {
      set({ appInfo });
    });
    get().setOtherListener("appInfo", unsubscribe);
  },
});
